/*!
 * __audioBackground — shared primitives that keep WebAudio tools alive in
 * background tabs (user report 2026-07-10: endless-piano died on tab switch;
 * the whole ambient family — white-noise, binaural-beats, isochronic-tones,
 * ocean-waves, metronome — has the same disease).
 *
 * Two independent legs, use BOTH for etalon background behavior:
 *
 * 1) createClock(fn, intervalMs) — scheduler tick source running in a
 *    dedicated Worker. Main-thread setInterval is throttled to once/minute in
 *    background Chromium (and worse in power-saver browsers); Worker timers
 *    are not visibility-throttled. Falls back to setInterval when Workers are
 *    unavailable — callers should ALSO grow their look-ahead when
 *    document.hidden as a second belt.
 *
 * 2) routeToElement(ctx, sourceNode) — reroutes a WebAudio graph's output
 *    into a hidden <audio> element via MediaStreamDestination. A tab playing
 *    a media element gets the browser's full "playing media" treatment:
 *    audible-tab exemption from throttling, MediaSession/lock-screen
 *    integration, iOS screen-lock survival and ring-switch bypass. Must be
 *    called from a user gesture (element.play() needs activation).
 *    IMPORTANT: caller must ensure sourceNode is NOT also connected to
 *    ctx.destination, or the user hears doubled audio.
 *
 * 3) holdScheduled(ctx, name) — объявляет «у этого контекста ЗАБРОНИРОВАН звук
 *    в будущем, не усыплять»: помечает контекст для нашего же хука экономии
 *    батареи в timbrica-core.js (suspendAllIfIdle). Считается по именам,
 *    снимается release(). Жест не нужен — это пометка, а не воспроизведение.
 *
 * ES5-safe, no build step.
 */
(function () {
'use strict';

/* Цикл часов — то же, что public/js/audio-clock-worker.js (тот файл остаётся
   для страниц со старой копией этого модуля в кэше). */
var CLOCK_SRC = "var t=null;onmessage=function(e){var d=e.data||{};" +
    "if(d.cmd==='start'){if(t)clearInterval(t);t=setInterval(function(){postMessage('tick')},d.interval||250);postMessage('tick')}" +
    "else if(d.cmd==='stop'){if(t){clearInterval(t);t=null}}};";

/* ── удержание «звук забронирован» ──────────────────────────────────────────
   Только пометка контекста — и это не упрощение, а замер.

   До 08.10.2026 удержание держало ещё и приглушённый зацикленный <audio> в
   документе: «страница играет медиа» для браузера плюс предикат
   anyMediaPlaying нашего хука. Скрытую вкладку оно не держало НИКОГДА:
   Chromium сам ставит на паузу приглушённое медиа фоновой вкладки — замер в
   настоящем Chrome 155 без обвязки Playwright (сырой CDP, вкладку прячет
   переключение на другую): событие `pause` пришло через 17 мс после
   `visibilitychange`, без единого вызова pause() из нашего кода. Сторож каждые
   5 с запускал его снова, браузер снова гасил — 124 260 событий
   `audio_hold repair` из 146 610 событий /school-bell за две недели (RU,
   24.09–08.10), до 8,5 тыс. на одну учительскую сессию. Звонок при этом
   звучал: в тех же сессиях 18–24 звонка в скрытой вкладке и ни одного
   пропуска, — потому что хук не трогает помеченный контекст, а сам Chrome на
   компьютере фоновый контекст не усыпляет. Работала пометка, элемент только
   шумел (и отдавал телеметрии фантомный `tool_complete` src:blob 4044 Б).

   Почему не «звучащий» элемент вместо приглушённого: льгота Chrome для
   фоновой вкладки даётся за СЛЫШИМЫЙ звук (уровень на выходе, а не флаг
   muted), тишина её не получает; а незаглушённый элемент на Android забирает
   аудиофокус и ставит на паузу чужую музыку. Телефон со скрытой вкладкой
   звонок не удержит ничем из страницы — это честно сказано в FAQ тула. */

window.__audioBackground = {

    /* Объявить, что у контекста забронирован звук в будущем. Возвращает
       { release(), held() }; каждое удержание снимается своим release(),
       контекст спит снова, когда снято последнее. Второй аргумент (имя тула)
       прежние вызовы передают — он больше ни на что не влияет. */
    holdScheduled: function (ctx) {
        if (!ctx) return { release: function () { }, held: function () { return false; } };
        try { ctx.__tmbScheduled = (ctx.__tmbScheduled || 0) + 1; } catch (x) { }
        var released = false;
        return {
            held: function () {
                try { return !released && ctx.state !== 'closed'; } catch (x) { return false; }
            },
            release: function () {
                if (released) return;
                released = true;
                try { ctx.__tmbScheduled = Math.max(0, (ctx.__tmbScheduled || 1) - 1); } catch (x) { }
            }
        };
    },

    /* Worker-based clock immune to background timer throttling.
       Returns { stop(), usingWorker }.

       ⚠️ Часы, которые не тикнули, — не часы. Замер 21.10.2026 на /midi: скрипт
       воркера, запрошенный по адресу, идёт через маршрут воркеров service
       worker'а, и часть запросов не получала ответа НИКОГДА — ни сообщения,
       ни события error, поэтому прежний запасной таймер не включался, а
       планировщик с упреждением играл своё первое окно и дальше молчал под
       «играет» (в 1 загрузке из 6 молчали все часы страницы). Теперь:
       воркер собирается из исходника ниже (Blob, без сети и без SW), и пока
       он не тикнул, через max(1 с, 4 интервала) ход берёт таймер главного
       потока — первый тик воркера его гасит. */
    createClock: function (fn, intervalMs) {
        var ms = intervalMs || 250, stopped = false, worker = null, fallbackId = null;
        var alive = false, url = null, guard = null;
        function fallback() { if (!stopped && fallbackId === null) fallbackId = setInterval(fn, ms); }
        try {
            url = URL.createObjectURL(new Blob([CLOCK_SRC], { type: 'text/javascript' }));
            worker = new Worker(url);
            worker.onmessage = function () {
                if (stopped) return;
                if (!alive) {
                    alive = true;
                    if (fallbackId !== null) { clearInterval(fallbackId); fallbackId = null; }
                }
                fn();
            };
            worker.onerror = function () {
                /* worker blocked → degrade to main-thread timer */
                try { worker.terminate(); } catch (e) {}
                worker = null;
                fallback();
            };
            worker.postMessage({ cmd: 'start', interval: ms });
            guard = setTimeout(function () { if (!alive) fallback(); }, Math.max(1000, 4 * ms));
        } catch (e) {
            worker = null;
            fallback();
        }
        return {
            get usingWorker() { return !!worker && alive; },
            stop: function () {
                stopped = true;
                if (guard !== null) { clearTimeout(guard); guard = null; }
                if (worker) { try { worker.postMessage({ cmd: 'stop' }); worker.terminate(); } catch (e) {} worker = null; }
                if (fallbackId !== null) { clearInterval(fallbackId); fallbackId = null; }
                if (url) { try { URL.revokeObjectURL(url); } catch (e) {} url = null; }
            }
        };
    },

    /* Route sourceNode → MediaStreamDestination → hidden <audio>.
       Returns { element, ok, stop() } ; ok=false → caller keeps/falls back to
       the direct ctx.destination connection. */
    routeToElement: function (ctx, sourceNode) {
        try {
            if (!ctx || !sourceNode || !ctx.createMediaStreamDestination) return { ok: false };
            var msd = ctx.createMediaStreamDestination();
            sourceNode.connect(msd);
            var el = document.createElement('audio');
            el.setAttribute('aria-hidden', 'true');
            el.style.display = 'none';
            el.srcObject = msd.stream;
            el.volume = 1;
            document.body.appendChild(el);
            var playP = el.play();
            if (playP && playP.catch) playP.catch(function () {});
            return {
                ok: true,
                element: el,
                resume: function () { try { var p = el.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {} },
                pause: function () { try { el.pause(); } catch (e) {} },
                stop: function () {
                    try { el.pause(); el.srcObject = null; el.remove(); } catch (e) {}
                    try { sourceNode.disconnect(msd); } catch (e) {}
                }
            };
        } catch (e) {
            return { ok: false };
        }
    }
};

})();
